import { useEffect, useMemo, useState } from "react";
import { Modal } from "@/common/components/modal";
import { Button } from "@/common/components/button";
import { INVENTORY_DISCOUNT_PATH, INVENTORY_URL } from "@/common/constants";
import { KeyValueObj } from "@/common/types";
import { IBatchDiscount } from "@/common/types/pricing";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { formatToNaira } from "@/common/utils/general";
import { formatQuantity } from "@/common/utils/quantity";

interface IBatchDiscountModalProps {
  isOpen: boolean;
  close: () => void;
  batch: KeyValueObj | null;
  onUpdated: () => void;
}

const MIN_DISCOUNT = 1;
const MAX_DISCOUNT = 99;

// MED-659 (UC-LST-02) — a clearance discount on one batch.
//
// Only the percentage is ever sent. The effective price is derived from the
// product's listing, so repricing the product moves every discounted batch with
// no write here — which is why this modal shows the listing price it derives
// from rather than letting the seller type an absolute discounted price.
export function BatchDiscountModal({ isOpen, close, batch, onUpdated }: IBatchDiscountModalProps) {
  const apiCall = useApiCallHandling();

  const existing: IBatchDiscount | undefined = batch?.discount;
  const [percentage, setPercentage] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setPercentage(existing?.discount_percentage ? String(existing.discount_percentage) : "");
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, batch?.id]);

  const listingPrice = Number(existing?.price_per_base_unit ?? 0);

  const preview = useMemo(() => {
    const pct = Number(percentage);
    if (!percentage || Number.isNaN(pct) || pct < MIN_DISCOUNT || pct > MAX_DISCOUNT) return null;
    if (!listingPrice) return null;
    return listingPrice * (1 - pct / 100);
  }, [percentage, listingPrice]);

  const discountUrl = batch?.id ? `${INVENTORY_URL}/${batch.id}${INVENTORY_DISCOUNT_PATH}` : "";

  function submit(method: "PUT" | "DELETE") {
    if (!batch?.id) return;

    if (method === "PUT") {
      const pct = Number(percentage);
      if (!percentage.trim() || Number.isNaN(pct) || pct < MIN_DISCOUNT || pct > MAX_DISCOUNT) {
        setError(`Enter a discount between ${MIN_DISCOUNT} and ${MAX_DISCOUNT}.`);
        return;
      }
    }

    setSaving(true);
    setError("");
    apiCall(
      discountUrl,
      method,
      method === "PUT" ? { discount_percentage: percentage } : {},
      () => {
        onUpdated();
        close();
      },
      (err: any) => setError(err?.response?.data?.message || "Could not save this discount."),
      () => setSaving(false),
      { dontNotifyonFail: true },
    );
  }

  // The backend refuses a discount on an unpriced product; say so up front
  // rather than letting the seller type a number that cannot be saved.
  const unpriced = !listingPrice;

  return (
    <Modal isOpen={isOpen} close={close} title="Set batch discount">
      <div className="w-[30rem] max-w-[90vw] px-4 py-2">
        {batch && (
          <>
            <div className="mb-4 rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
              <p className="font-semibold text-gray-800">Batch {batch.batch_number}</p>
              <p>Expires {batch.expiry_date}</p>
              {/* MED-697: "1 Carton available", not "10 available". */}
              <p>{formatQuantity(batch.quantity_display?.in_stock, batch.quantity_in_stock)} available</p>
            </div>

            {unpriced ? (
              <p className="py-4 text-sm text-amber-700">
                Set a selling price for this product before discounting a batch — a discount is a
                percentage of the product&apos;s price.
              </p>
            ) : (
              <>
                <p className="mb-3 text-xs text-gray-600">
                  Current price {formatToNaira(Number(listingPrice.toFixed(2)))} per unit
                </p>

                <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="discount">
                  Discount (%)
                </label>
                <input
                  id="discount"
                  type="number"
                  min={MIN_DISCOUNT}
                  max={MAX_DISCOUNT}
                  value={percentage}
                  onChange={(e) => {
                    setPercentage(e.target.value);
                    setError("");
                  }}
                  placeholder={`${MIN_DISCOUNT}–${MAX_DISCOUNT}`}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                />

                <div className="mt-2 min-h-[1.5rem] text-xs">
                  {preview !== null && (
                    <p className="text-gray-700">
                      {formatToNaira(Number(listingPrice.toFixed(2)))} − {percentage}% ={" "}
                      <span className="font-semibold">
                        {formatToNaira(Number(preview.toFixed(2)))}
                      </span>{" "}
                      per unit
                    </p>
                  )}
                </div>

                {error && <p className="mt-1 text-xs text-red-600">{error}</p>}

                <div className="mt-5 flex items-center justify-end gap-3">
                  {existing?.discounted && (
                    <button
                      type="button"
                      onClick={() => submit("DELETE")}
                      disabled={saving}
                      className="text-sm font-medium text-red-600 underline"
                    >
                      Remove discount
                    </button>
                  )}
                  <Button onClick={() => submit("PUT")} disabled={saving}>
                    {saving ? "Saving…" : "Save discount"}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
